import React, { useEffect, useRef, useState } from "react";
import downIcon from "../../../asset/images/download-blue-arrow.svg";
import filter from "../../../asset/images/preference-horizontal.svg";
import totalReqIcon from "../../../asset/images/fileUpload.svg";
import pendingIcon from "../../../asset/images/timePending.svg";
import approvedIcon from "../../../asset/images/approvedConf.svg";
import rejectedIcon from "../../../asset/images/crossIcon.svg";
import sendBackIcon from "../../../asset/images/sentback-pending.svg";
import { Modal } from "react-bootstrap";
import { Dealer_Mapping } from "../../../Configuration/API_URL";
import infoIcon from "../../../asset/images/info-circle.svg";
import { batchIdFormatter, batchIdPaste, decryptData, formatDate, noDataFound, toSentenceCase } from "../../../CommonMethhods/CommonMethods";
import { toast } from "react-toastify";
import axiosInstance from "../../../CommonMethhods/axiosInstance";
import { useDebounce } from "../../../Components/useDebounce";
import moment from "moment";
import Loader from "../../../Components/Common/Loader";
import SmallLoader from "../../../Components/Common/smallLoader";
import CustomTradeDate from "../../../CommonMethhods/CustomDate";
import { useNavigate } from "react-router-dom";

// Collapses spelling variants, e.g. "sendback" / "send_back" / "Send Back".
const normalizeStatus = (status = "") =>
  (status || "").toLowerCase().replace(/[\s_-]/g, "");

const batchKey = (item) => String(item?.batchId || "").toUpperCase();

// The checker-requests list doesn't include batches that are still waiting on
// the checker, so those come from pending-batches (the Pending Approvals
// queue) and are reshaped into the same row format as the history grid.
const fetchPendingBatches = async () => {
  try {
    const response = await axiosInstance.get(Dealer_Mapping.checkerPreview);
    const decrypted = JSON.parse(decryptData(response?.data));
    if (decrypted?.status !== 0) return [];

    return (decrypted?.data || []).map((batch) => ({
      ...batch,
      id: batch?.batchId,
      requestDate: batch?.submittedAt,
      count: batch?.totalValidCount || batch?.requests?.length || 0,
      status: "pending",
    }));
  } catch (error) {
    return [];
  }
};

// Client-side version of the type / date / search filters, for the pending
// batches which the backend doesn't filter.
const matchesFilters = (item, { search, type, from, to }) => {
  const isBulk = batchKey(item).startsWith("BLK");
  if (type === "single" && isBulk) return false;
  if (type === "bulk" && !isBulk) return false;

  const date = moment(item?.requestDate);
  if (from && date.isBefore(moment(from, "DD-MM-YYYY"), "day")) return false;
  if (to && date.isAfter(moment(to, "DD-MM-YYYY"), "day")) return false;

  const text = (search || "").trim().toLowerCase();
  return !text || batchKey(item).toLowerCase().includes(text);
};

function CheckerMyRequest({ setActiveTab, setViewRequest }) {
  const [search, setSearch] = useState("");
  const [filterModal, setFilterModal] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [cardCounts, setCardCounts] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    sendback: 0,
    rejected: 0,
  });
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loader, setLoader] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [downloadLoader, setDownloadLoader] = useState(false);
  const resetBtnRef = useRef(null);
  const applyBtnRef = useRef(null);
  const headerRefs = useRef([]);
  const allTypeRef = useRef(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const navigate = useNavigate();
  const debouncedValue = useDebounce(search, 1000);

  useEffect(() => {
    getRequestHistory(
      "grid",
      debouncedValue,
      fromDate,
      toDate,
      statusFilter,
      typeFilter
    );
  }, [debouncedValue]);

  useEffect(() => {
    setPage(1);
  }, [historyData]);

  useEffect(() => {
    getCardCounts();
  }, []);

  const getCardCounts = async () => {
    try {
      const queryParams = new URLSearchParams({
        format: "grid",
        status: "all",
        type: "all",
        fromDate: "",
        toDate: "",
        batchIdRef: "",
      });
      const [historyRes, pendingList] = await Promise.all([
        axiosInstance
          .get(`${Dealer_Mapping.checkerMyRequest}?${queryParams}`)
          .then((response) => JSON.parse(decryptData(response?.data)))
          .catch(() => null),
        fetchPendingBatches(),
      ]);

      const list = historyRes?.status === 0 ? historyRes?.data || [] : [];
      const pendingKeys = new Set(pendingList.map(batchKey));
      const extraPending = pendingList.length;
      const total = list.filter((item) => !pendingKeys.has(batchKey(item))).length + extraPending;

      const counts = { total, pending: extraPending, approved: 0, sendback: 0, rejected: 0 };

      list.forEach((item) => {
        const status = normalizeStatus(item?.status);
        if (status === "approved") counts.approved += 1;
        else if (status === "sendback") counts.sendback += 1;
        else if (status === "rejected") counts.rejected += 1;
      });

      setCardCounts(counts);
    } catch (error) {
      console.log(error);
    }
  };

  const handleCardFilter = (status) => {
    setStatusFilter(status);
    getRequestHistory("grid", search, fromDate, toDate, status, typeFilter);
  };

  const showFilterModal = () => {
    setFilterModal(true);
  };

  // Request details live on their own route so the page survives a refresh
  // or a directly opened URL - the row is passed along only to avoid a flash
  // while the details page re-fetches it.
  const handleViewButton = (row) => {
    if (!row?.batchId) return;

    navigate(`/dealer-mapping/checker-request/${row.batchId}`, {
      state: { request: row },
    });
  };

  const getRequestHistory = async (
    format = "grid",
    searchText = "",
    from = fromDate,
    to = toDate,
    status = statusFilter,
    type = typeFilter
  ) => {

    if (format === "grid") {
      setLoader(true);
    } else {
      setDownloadLoader(true);
    }

    try {
      const queryParams = new URLSearchParams({
        format,
        // "pending" isn't a backend status filter - pending batches come from
        // pending-batches instead (see fetchPendingBatches).
        status:
          status?.toLowerCase() === "all" || status?.toLowerCase() === "pending"
            ? "all"
            : status.toLowerCase(),
        type: type.toLowerCase() || "all",
        fromDate: from
          ? moment(from, "DD-MM-YYYY").format("YYYY-MM-DD")
          : "",
        toDate: to
          ? moment(to, "DD-MM-YYYY").format("YYYY-MM-DD")
          : "",
        batchIdRef: searchText,
      });

      if (format !== "grid") {
        const response = await axiosInstance.get(
          `${Dealer_Mapping.checkerMyRequest}?${queryParams}`,
        );
        const decrypted = JSON.parse(decryptData(response.data));
        if (decrypted?.status === 0) {
          toast.success(decrypted?.message);
          window.open(decrypted?.data?.url, "_self", "noopener,noreferrer");
        } else {
          toast.error(decrypted.message);
        }
        return;
      }

      const wantsPending = ["all", "pending"].includes(status?.toLowerCase());

      // A failed / empty history list must not hide the pending batches.
      let historyMessage = "";
      const historyPromise =
        status?.toLowerCase() === "pending"
          ? Promise.resolve([])
          : axiosInstance
              .get(`${Dealer_Mapping.checkerMyRequest}?${queryParams}`)
              .then((response) => {
                const decrypted = JSON.parse(decryptData(response.data));
                if (decrypted?.status === 0) return decrypted.data || [];
                historyMessage = decrypted?.message;
                return [];
              })
              .catch((error) => {
                try {
                  historyMessage = JSON.parse(decryptData(error.response.data)).message;
                } catch (e) {
                  historyMessage = "Something went wrong";
                }
                return [];
              });

      const [historyList, pendingList] = await Promise.all([
        historyPromise,
        wantsPending ? fetchPendingBatches() : Promise.resolve([]),
      ]);

      const filters = { search: searchText, type: type.toLowerCase(), from, to };
      const pendingMatches = pendingList.filter((item) => matchesFilters(item, filters));

      // Where a batch is in both lists, keep the history row's batch-level
      // reason / remarks on the pending version.
      const historyByKey = {};
      historyList.forEach((item) => {
        historyByKey[batchKey(item)] = item;
      });
      const pendingRows = pendingMatches.map((item) => {
        const match = historyByKey[batchKey(item)];
        return match
          ? { ...match, ...item, reason: match.reason, remarks: match.remarks }
          : item;
      });

      let rows;
      if (status?.toLowerCase() === "pending") {
        rows = pendingRows;
      } else if (status?.toLowerCase() === "all") {
        const pendingKeys = new Set(pendingRows.map(batchKey));
        rows = [...pendingRows, ...historyList.filter((item) => !pendingKeys.has(batchKey(item)))];
      } else {
        rows = historyList;
      }

      setHistoryData(rows);
      if (rows.length === 0 && historyMessage) toast.error(historyMessage);
    } catch (error) {
      const decryptedError = JSON.parse(
        decryptData(error.response.data)
      );
      toast.error(decryptedError.message);
    } finally {
      if (format === "grid") {
        setLoader(false);
      } else {
        setDownloadLoader(false);
      }
    }
  };

  const currentDate = new Date();
  const yyyy = currentDate.getFullYear();
  let mm = currentDate.getMonth() + 1;
  let dd = currentDate.getDate();
  if (dd < 10) dd = "0" + dd;
  if (mm < 10) mm = "0" + mm;
  const FormatedDate = dd + "/" + mm + "/" + yyyy;
  const DateReverse = FormatedDate.split("/").reverse().join("-");

  const handleResetFilter = () => {
    setFromDate("");
    setToDate("");
    setTypeFilter("all");

    getRequestHistory(
      "grid",
      search,
      "",
      "",
      statusFilter,
      "all"
    );
  };

  const handleFilterKeyDown = (e) => {
    if (e.key !== "Enter") return;

    if (e.target instanceof HTMLButtonElement) {
      return;
    }

    e.preventDefault();
    handleApplyFilter();
  };

  const handleApplyFilter = (selectedType = typeFilter) => {
    if (!fromDate && !toDate) {
      getRequestHistory(
        "grid",
        search,
        "",
        "",
        statusFilter,
        selectedType
      );
      setFilterModal(false);
      return;
    }

    if (!fromDate || !toDate) {
      toast.error("Please select both From Date and To Date");
      return;
    }

    const from = moment(fromDate, "DD-MM-YYYY", true);
    const to = moment(toDate, "DD-MM-YYYY", true);
    const today = moment().startOf("day");

    if (!from.isValid() || !to.isValid()) {
      toast.error("Please enter valid dates");
      return;
    }

    if (from.isAfter(today)) {
      toast.error("From Date cannot be a future date");
      return;
    }

    if (to.isAfter(today)) {
      toast.error("To Date cannot be a future date");
      return;
    }

    if (to.isBefore(from)) {
      toast.error("To Date cannot be earlier than From Date");
      return;
    }

    if (to.diff(from, "days") > 31) {
      toast.error("Date range should not exceed 1 month");
      return;
    }

    getRequestHistory(
      "grid",
      search,
      fromDate,
      toDate,
      statusFilter,
      selectedType
    );

    setFilterModal(false);
  };
  const isDownloadDisabled = historyData?.length === 0;

  const handleCloseFilter = () => {
    setTypeFilter("all")
    setFromDate("");
    setToDate("");
    setFilterModal(false);
  };

    const getStatusClass = (status = "") => {
    switch (normalizeStatus(status)) {
      case "pending":
        return "status-pending";
      // case "partial":
      //   return "status-pending";
      case "approved":
        return "status-approved";
      case "rejected":
        return "status-rejected";
      case "sendback":
        return "status-sendback";
      default:
        return "";
    }
  };

  const getStatusLabel = (status = "") => {
    const normalized = normalizeStatus(status);
    // if (normalized === "partial") return "Partial";
    if (normalized === "sendback") return "Send Back";
    return toSentenceCase(status);
  };

  useEffect(() => {
    if (filterModal) {
      allTypeRef.current?.focus();
    }
  }, [filterModal]);

  const typeRefs = useRef([]);

  const typeOptions = ["all", "single", "bulk"];

  const handleTypeKeyDown = (e, index) => {
    let nextIndex = index;

    switch (e.key) {
      case "ArrowRight":
        e.preventDefault();
        nextIndex = (index + 1) % typeOptions.length;
        setTypeFilter(typeOptions[nextIndex]);
        typeRefs.current[nextIndex]?.focus();
        return;

      case "ArrowLeft":
        e.preventDefault();
        nextIndex =
          (index - 1 + typeOptions.length) % typeOptions.length;
        setTypeFilter(typeOptions[nextIndex]);
        typeRefs.current[nextIndex]?.focus();
        return;

      case "Enter":
      case " ":
        e.preventDefault();

        const selectedType = typeOptions[index];

        setTypeFilter(selectedType);

        handleApplyFilter(selectedType);

        return;

      default:
        return;
    }
  };

  const handleFooterKeyDown = (e) => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      applyBtnRef.current?.focus();
      return;
    }

    if (e.key === "ArrowLeft") {
      e.preventDefault();
      resetBtnRef.current?.focus();
      return;
    }

    if (e.key === "Enter") {
      e.stopPropagation();
      return;
    }
  };

  const handleHeaderKeyDown = (e, index) => {
  switch (e.key) {
    case "ArrowRight":
      e.preventDefault();
      headerRefs.current[Math.min(index + 1, headerRefs.current.length - 1)]?.focus();
      break;

    case "ArrowLeft":
      e.preventDefault();
      headerRefs.current[Math.max(index - 1, 0)]?.focus();
      break;

    case "Enter":
    case " ":
      e.preventDefault();
      headerRefs.current[index]?.click();
      break;

    default:
      break;
  }
};

  const totalCount = historyData?.length || 0;
  const paginatedData = historyData.slice((page - 1) * limit, page * limit);
  const totalPages = Math.ceil(totalCount / limit);

  const getPageNumbers = (current, total) => {
    const delta = 1;
    const pages = [];

    for (let i = 1; i <= total; i++) {
      if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
        pages.push(i);
      }
    }

    const pagesWithEllipsis = [];
    let previous = null;
    pages.forEach((i) => {
      if (previous !== null && i - previous > 1) {
        pagesWithEllipsis.push("...");
      }
      pagesWithEllipsis.push(i);
      previous = i;
    });

    return pagesWithEllipsis;
  };

  return (
    <>
      <div className="off-market-main">
        <div className="ckr-page new-upload">

          <div className="ckr-queue-wrapper">
            <div className="ckr-stats">
              <div
                className={`ckr-stat-card status-card ${statusFilter === "all" ? "ckr-stat-active" : ""}`}
                role="button"
                tabIndex={0}
                onClick={() => handleCardFilter("all")}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleCardFilter("all"); } }}
              >
                <img src={totalReqIcon} alt="" className="ckr-stat-icon-img" />
                <div className="ckr-stat-info d-flex flex-column">
                  <span className="status-title">Total Requests</span>
                  <span className="ckr-stat-value">{cardCounts.total}</span>
                </div>
              </div>

              <div
                className={`ckr-stat-card status-card ${statusFilter === "pending" ? "ckr-stat-active" : ""}`}
                role="button"
                tabIndex={0}
                onClick={() => handleCardFilter("pending")}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleCardFilter("pending"); } }}
              >
                <img src={pendingIcon} alt="" className="ckr-stat-icon-img" />
                <div className="ckr-stat-info d-flex flex-column">
                  <span className="status-title">Approval Pending</span>
                  <span className="ckr-stat-value ckr-stat-value--queue">{cardCounts.pending}</span>
                </div>
              </div>

              <div
                className={`ckr-stat-card status-card ${statusFilter === "approved" ? "ckr-stat-active" : ""}`}
                role="button"
                tabIndex={0}
                onClick={() => handleCardFilter("approved")}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleCardFilter("approved"); } }}
              >
                <div className="wrap-app-icon">
                  <img src={approvedIcon} alt="" className="ckr-stat-icon-img" />
                </div>
                <div className="ckr-stat-info d-flex flex-column">
                  <span className="status-title">Approved</span>
                  <span className="ckr-stat-value ckr-stat-value--approved">{cardCounts.approved}</span>
                </div>
              </div>

              <div
                className={`ckr-stat-card status-card ${statusFilter === "sendback" ? "ckr-stat-active" : ""}`}
                role="button"
                tabIndex={0}
                onClick={() => handleCardFilter("sendback")}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleCardFilter("sendback"); } }}
              >
                <img src={sendBackIcon} alt="" className="ckr-stat-icon-img" />
                <div className="ckr-stat-info d-flex flex-column">
                  <span className="status-title">Send back</span>
                  <span className="ckr-stat-value ckr-stat-value--sendback">{cardCounts.sendback}</span>
                </div>
              </div>

              <div
                className={`ckr-stat-card status-card ${statusFilter === "rejected" ? "ckr-stat-active" : ""}`}
                role="button"
                tabIndex={0}
                onClick={() => handleCardFilter("rejected")}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleCardFilter("rejected"); } }}
              >
                <img src={rejectedIcon} alt="" className="ckr-stat-icon-img" />
                <div className="ckr-stat-info d-flex flex-column">
                  <span className="status-title">Rejected</span>
                  <span className="ckr-stat-value ckr-stat-value--rejected">{cardCounts.rejected}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="ckr-section history-table">
            <div className="ckr-section-header">
              <p className="ckr-section-title">Request History</p>

              <div className="d-flex align-items-center header-filter">

                <div className="filter-options offmark-filter">
                  <div className="dropdown-filter focusable-filter">
                    <select
                      size="lg"
                      aria-label="Default select example"
                      value={statusFilter}
                      onChange={(e) => {
                        const value = e.target.value;
                        setStatusFilter(value);
                        getRequestHistory(
                          "grid",
                          search,
                          fromDate,
                          toDate,
                          value,
                          typeFilter
                        );
                      }}
                    >
                      <option value="all">All</option>
                      <option value="pending">Pending</option>
                      {/* <option value="partial">Partial</option> */}
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                      <option value="sendback">Send Back</option>
                    </select>
                  </div>
                </div>

                 <div className="filter-wrapper focusable-filter"
                  tabIndex={0}
                  ref={(el) => (headerRefs.current[1] = el)}
                  role="button"
                  onClick={showFilterModal}
                  onKeyDown={(e) => handleHeaderKeyDown(e, 1)}>
                    <img src={filter} alt="filter" />
                  </div>
                <div className="ckr-search-box focusable-filter">
                  <i className="fa fa-search" />
                  <input
                  ref={(el) => (headerRefs.current[2] = el)}
                    onKeyDown={(e) => handleHeaderKeyDown(e, 2)}
                    type="text"
                    placeholder="Search by client code/name"
                    value={search}
                    maxLength={10}
                    onChange={(e) => {
                      setSearch(batchIdFormatter(e?.target?.value))
                    }}
                    onPaste={() => {
                      batchIdPaste(10)
                    }}
                  />
                </div>

                <div className={`wrap-down-btn focusable-filter ${isDownloadDisabled ? "disabled-btn-download" : ""}`}
                 role="button"
                //  disabled={isDownloadDisabled}
                  tabIndex={isDownloadDisabled ? -1 : 0}
                  onClick={() =>{
                     if (isDownloadDisabled) return;

                    getRequestHistory(
                      "file",
                      search,
                      fromDate,
                      toDate,
                      statusFilter,
                      typeFilter
                    )}
                  }
                  ref={(el) => (headerRefs.current[3] = el)}
                  onKeyDown={(e) => handleHeaderKeyDown(e, 3)}>

                  {downloadLoader ?
                    <SmallLoader />
                    :
                    <>
                      <img src={downIcon} alt="download-icon" />

                      <button
                        type="button"
                        className={`download-template ${isDownloadDisabled ? "disabled-text" : ""}`}
                        disabled={isDownloadDisabled}>Download</button>
                    </>}
                </div>

              </div>
            </div>
            <div className="ckr-table-wrapper cdm-table">
              {loader ?
                <div className="loader-alignment">
                  <Loader />
                </div>
                :
                historyData?.length > 0 ?
                  <>
                  <table className="ckr-table draft-maker">
                    <thead>
                      <tr>
                        <th>SR</th>
                        <th>Request Date</th>
                        <th>Total Records</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedData.map((data) => (
                        <tr key={data?.id}>
                          <td>{data?.batchId || "-"}</td>
                          <td>{formatDate(data?.requestDate) || "-"}</td>
                          <td>{data.count || 0}</td>
                          <td>
                            {data?.status ? (
                              <span className={getStatusClass(data?.status)}>
                                {getStatusLabel(data?.status)}
                              </span>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td>
                            <div className="view-btn">
                              <button
                                className="submit-btns-view"
                                onClick={() => handleViewButton(data)}
                              >
                                View Details
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div className="d-flex align-items-center justify-content-between pagination-wrapper">
                    <div className="pagination-left d-flex align-items-center">
                      <span className="items-label">Items per page</span>

                      <select
                        value={limit}
                        onChange={(e) => {
                          setLimit(Number(e.target.value));
                          setPage(1);
                        }}
                        className="page-select"
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>

                      <span className="page-count">
                        {totalCount === 0
                          ? "0-0 items"
                          : `${(page - 1) * limit + 1}-${Math.min(page * limit, totalCount)} of ${totalCount} items`}
                      </span>
                    </div>

                    <div className="pagination-right d-flex align-items-center">
                      <button
                        className="pagination-btn"
                        disabled={page === 1}
                        onClick={() => setPage((prev) => prev - 1)}
                      >
                        &lt;
                      </button>

                      {getPageNumbers(page, totalPages).map((item, index) =>
                        item === "..." ? (
                          <span key={`dots-${index}`} className="pagination-dots">
                            ...
                          </span>
                        ) : (
                          <button
                            key={item}
                            className={`pagination-number ${page === item ? "active-page" : ""}`}
                            onClick={() => setPage(item)}
                          >
                            {item}
                          </button>
                        )
                      )}

                      <button
                        className="pagination-btn"
                        disabled={page === totalPages}
                        onClick={() => setPage((prev) => prev + 1)}
                      >
                        &gt;
                      </button>
                    </div>
                  </div>
                  </>
                  :
                  <div className="step-content report-error-message">
                    {noDataFound()}
                  </div>
              }
            </div>
          </div>
        </div>
      </div>

      <Modal
        show={filterModal}
        onHide={() => setFilterModal(false)}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        className="inactive-modal setting-model cdm-date-modal"
        backdrop="static"
        keyboard={true}
      >
        <Modal.Header className="mb-4 modal-header">
          <Modal.Title id="contained-modal-title-vcenter modal-title">
            <div className="figure-inlac header-pnl">
              <span className="header-title">Filters</span>
            </div>
          </Modal.Title>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={handleCloseFilter}
          >
            <span className="icon-close"></span>
          </button>
        </Modal.Header>
        <Modal.Body>
          <div className="table-responsive scroll-table model-top-active-table"
            onKeyDown={handleFilterKeyDown}>
            <span className="body-title-main">Select Type</span>
            <div className="type-wrapper"
              role="radiogroup"
              aria-label="Select Type">
              <button
                className={`type-button ${typeFilter === "all" ? "active" : ""
                  }`}
                type="button"
                role="radio"
                aria-checked={typeFilter === "all"}
                ref={allTypeRef}
                onKeyDown={(e) => handleTypeKeyDown(e, 0)}
                onClick={() => setTypeFilter("all")}
              >
                All type
              </button>

              <button
                role="radio"
                aria-checked={typeFilter === "single"}
                ref={(el) => (typeRefs.current[1] = el)}
                onKeyDown={(e) => handleTypeKeyDown(e, 1)}
                className={`type-button ${typeFilter === "single" ? "active" : ""}`}
                type="button"
                onClick={() => setTypeFilter("single")}
              >
                Single
              </button>

              <button
                role="radio"
                aria-checked={typeFilter === "bulk"}
                ref={(el) => (typeRefs.current[2] = el)}
                onKeyDown={(e) => handleTypeKeyDown(e, 2)}
                className={`type-button ${typeFilter === "bulk" ? "active" : ""}`}
                type="button"
                onClick={() => setTypeFilter("bulk")}
              >
                Bulk
              </button>
            </div>
            <span className="body-title-main">Custom Date Range</span>

            <div className="date-wrapper">
              <div className="form-group">
                <label htmlFor="" className="form-label">
                  From Date<span className="manadate-mark">*</span>

                </label>
                <div>
                  <CustomTradeDate
                    idPrefix="fromDate"
                    value={fromDate}
                    onChange={(date) => {
                      setFromDate(date);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleApplyFilter();
                      }
                    }}
                    maxDate={toDate || DateReverse}
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="" className="form-label">
                  To Date<span className="manadate-mark">*</span>
                </label>
                <div>
                  <CustomTradeDate
                    idPrefix="toDate"
                    value={toDate}
                    onChange={(date) => {
                      setToDate(date);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleApplyFilter();
                      }
                    }}
                    minDate={fromDate}
                    maxDate={DateReverse}
                  />
                </div>
              </div>
            </div>

            <div className="info-banner">
              <img src={infoIcon} alt="info icon" />
              <span className="banner-text">Please select a date range where difference between From date and To date is less than 1 month</span>
            </div>

            <div className="footer-actions modal-footer">
              <button type="button"
                ref={resetBtnRef} className="back-btn" onClick={handleResetFilter}
                onKeyDown={handleFooterKeyDown}
              >Reset</button>
              <button type="button"
                ref={applyBtnRef}
                className="submit-btns"
                onClick={() => handleApplyFilter()}
                onKeyDown={handleFooterKeyDown}>
                Apply
              </button>
            </div>

          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default CheckerMyRequest;
